import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import type { Place } from '../../types';
import { MARKER_COLORS, PERSON_COLORS } from '../../utils/markerColor';
import { formatDate } from '../../utils/formatDate';

export const PLACES_LAYER_ID = 'places-layer';
const SOURCE_ID = 'places-source';

const VISIT_LABEL: Record<string, string> = { solo: 'Solo', family: 'Family', group: 'Group', markanya: 'Mark & Anya' };

function toGeoJSON(places: Place[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: places.map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.Longitude, p.Latitude] },
      properties: {
        PlaceId: p.PlaceId,
        PlaceName: p.PlaceName,
        Country: p.Country,
        myVisitType: p.myVisitType ?? '',
        myVisitDate: p.myVisitDate ?? '',
        myNotes: p.myNotes ?? '',
        mySoloTraveller: p.mySoloTraveller ?? '',
        myImageBlobURL: p.myImageBlobURL ?? '',
        AutoImageURL: p.AutoImageURL ?? '',
        PhotoURL1: p.PhotoURL1 ?? '',
      },
    })),
  };
}

function buildPopupHtml(props: Record<string, string>): string {
  const visitType = props.myVisitType;
  const color = visitType ? (MARKER_COLORS as Record<string, string>)[visitType] : '#9e9e9e';
  const badge = visitType
    ? `<span style="background:${color};color:#fff;font-size:11px;font-weight:600;border-radius:10px;padding:2px 8px;margin-left:6px;">${VISIT_LABEL[visitType] ?? visitType}</span>`
    : '';
  const date = props.myVisitDate
    ? `<div style="color:#757575;font-size:12px;margin-top:3px;">${formatDate(props.myVisitDate)}</div>`
    : '';
  const notes = props.myNotes
    ? `<div style="font-size:12px;color:#424242;margin-top:5px;max-width:200px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;">${props.myNotes}</div>`
    : '';
  const photo = props.PhotoURL1 || props.myImageBlobURL || props.AutoImageURL;
  const img = photo
    ? `<img src="${photo}" style="width:100%;height:90px;object-fit:cover;border-radius:4px;margin-bottom:6px;" />`
    : '';

  return `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:160px;">
      ${img}
      <div style="display:flex;align-items:center;flex-wrap:wrap;">
        <strong style="font-size:14px;">${props.PlaceName}</strong>${badge}
      </div>
      <div style="color:#9e9e9e;font-size:12px;">${props.Country}</div>
      ${date}${notes}
    </div>`;
}

interface PlaceMarkersProps {
  map: mapboxgl.Map | null;
  mapReady: boolean;
  places: Place[];
  onPlaceClick?: (place: Place) => void;
}

export function PlaceMarkers({ map, mapReady, places, onPlaceClick }: PlaceMarkersProps) {
  const placesRef = useRef(places);
  const onClickRef = useRef(onPlaceClick);
  placesRef.current = places;
  onClickRef.current = onPlaceClick;

  // Setup source, layer, and event handlers once
  useEffect(() => {
    if (!map || !mapReady) return;

    map.addSource(SOURCE_ID, { type: 'geojson', data: toGeoJSON([]) });

    map.addLayer({
      id: PLACES_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 5, 10, 9],
        'circle-color': [
          'case',
          ['==', ['get', 'myVisitType'], 'solo'],
          [
            'match', ['get', 'mySoloTraveller'],
            'Mark',     PERSON_COLORS.Mark,
            'Anya',     PERSON_COLORS.Anya,
            'Victoria', PERSON_COLORS.Victoria,
            'Liam',     PERSON_COLORS.Liam,
            MARKER_COLORS.solo,
          ],
          [
            'match', ['get', 'myVisitType'],
            'family',   MARKER_COLORS.family,
            'group',    MARKER_COLORS.group,
            'markanya', MARKER_COLORS.markanya,
            '#9E9E9E',
          ],
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 1,
      },
    });

    const popup = new mapboxgl.Popup({ closeButton: false, maxWidth: '260px', offset: 10 });

    const onMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features?.[0];
      if (!feature || !feature.geometry || feature.geometry.type !== 'Point') return;
      const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
      popup
        .setLngLat(coords)
        .setHTML(buildPopupHtml(feature.properties as Record<string, string>))
        .addTo(map);
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
      popup.remove();
    };

    const onLayerClick = (e: mapboxgl.MapLayerMouseEvent) => {
      e.originalEvent.stopPropagation();
      popup.remove();
      if (!onClickRef.current) return;
      const feature = e.features?.[0];
      if (!feature) return;
      const placeId = (feature.properties as Record<string, string>).PlaceId;
      const place = placesRef.current.find((p) => p.PlaceId === placeId);
      if (place) {
        if (feature.geometry.type === 'Point') {
          const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
          map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 7), duration: 800 });
        }
        onClickRef.current(place);
      }
    };

    map.on('mouseenter', PLACES_LAYER_ID, onMouseEnter);
    map.on('mouseleave', PLACES_LAYER_ID, onMouseLeave);
    map.on('click',      PLACES_LAYER_ID, onLayerClick);

    return () => {
      map.off('mouseenter', PLACES_LAYER_ID, onMouseEnter);
      map.off('mouseleave', PLACES_LAYER_ID, onMouseLeave);
      map.off('click',      PLACES_LAYER_ID, onLayerClick);
      popup.remove();
      if (map.getLayer(PLACES_LAYER_ID)) map.removeLayer(PLACES_LAYER_ID);
      if (map.getSource(SOURCE_ID))      map.removeSource(SOURCE_ID);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapReady]);

  // Update source data whenever places change
  useEffect(() => {
    if (!map || !mapReady) return;
    const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    source?.setData(toGeoJSON(places));
  }, [map, mapReady, places]);

  return null;
}
